<template>
  <button 
    :class="[
      $style.btn, 
      outline ? $style.outline : $style.filled, 
      sizeClass,
      { [$style.disabled]: disabled }
    ]"
    :disabled="disabled"
    @click="handleClick"
  >
    <template v-if="icon && iconPosition === 'left'">
      <component :is="icon" :class="$style.icon" />
    </template>
    <span>{{ text }}</span>
    <template v-if="icon && iconPosition === 'right'">
      <component :is="icon" :class="$style.icon" />
    </template>
  </button>
</template>

<script setup>
  import { defineProps, defineEmits, computed, useCssModule } from 'vue';
  
  const $style = useCssModule();
  const props = defineProps({
    text: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outline: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: [Object, Function],
      default: null,
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value),
    },
  });

  const emit = defineEmits(['click']);

  const handleClick = (event) => {
    if (!props.disabled) {
      emit('click', event);
    }
  };

  const sizeClass = computed(() => {
    switch (props.size) {
      case 'sm':
        return $style.small;
      case 'lg':
        return $style.large;
      default:
        return $style.medium;
    }
  });
</script>

<style module lang="scss">

  .btn {
    border: unset;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-family: $font-secondary;
    border-radius: 8px;
    font-weight: 400;
    transition: background 0.2s, color 0.2s;
  }

  .filled {
    background: #BBF90A;
    color: #232323;

    &:hover {
      background: #90C300;
    }
  }

  .outline {
    background: transparent;
    color: #BBF90A;
    border: 1px solid #BBF90A;

    &:hover {
      background: #BBF90A;
      color: #232323;
    }
  }

  .disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .small {
    font-size: 12px;
    padding: 6px 12px;
  }

  .medium {
    font-size: 16px;
    padding: 8px 16px;
  }

  .large {
    font-size: 18px;
    padding: 12px 20px;
  }

  .icon {
    width: 20px;
    height: 20px;
  }
</style>
